/**
 * Donut.tsx — recharts <PieChart> wrapper for category mixes.
 *
 * Pass an array of `{ name, value, color }`. Empty / all-zero arrays render
 * the EmptyState placeholder so callers don't have to guard.
 */

import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import EmptyState from './EmptyState';
import { formatCrore } from '../helpers/format';

export interface DonutDatum {
	name: string;
	value: number;
	color: string;
}

interface Props {
	data: DonutDatum[];
	height?: number;
	/** Format applied to the tooltip number. Defaults to ₹ Cr. */
	valueFormatter?: (v: number) => string;
	/** Inner radius (set 0 for a solid pie, > 0 for a donut). */
	innerRadius?: number;
}

export default function Donut({
	data,
	height = 240,
	valueFormatter = (v) => formatCrore(v, 1),
	innerRadius = 60,
}: Props) {
	const safeData = Array.isArray(data) ? data : [];
	const total = safeData.reduce((acc, d) => acc + (Number.isFinite(d.value) ? d.value : 0), 0);

	if (!safeData.length || total <= 0) {
		return <EmptyState title="No data" hint="Nothing to chart for this slice." />;
	}

	return (
		<div style={{ width: '100%', height }}>
			<ResponsiveContainer width="100%" height="100%">
				<PieChart>
					<Pie
						data={safeData}
						dataKey="value"
						nameKey="name"
						innerRadius={innerRadius}
						outerRadius={Math.max(innerRadius + 20, 90)}
						stroke="#fff"
						strokeWidth={2}
					>
						{safeData.map((d) => (
							<Cell key={d.name} fill={d.color} />
						))}
					</Pie>
					<Tooltip
						formatter={(v: number | string) =>
							typeof v === 'number' ? valueFormatter(v) : String(v)
						}
					/>
				</PieChart>
			</ResponsiveContainer>
		</div>
	);
}

/** Inline legend: colour swatch + name + value, used beside Donuts. */
export function DonutLegend({
	data,
	valueFormatter = (v) => formatCrore(v, 1),
}: {
	data: DonutDatum[];
	valueFormatter?: (v: number) => string;
}) {
	return (
		<ul
			style={{
				listStyle: 'none',
				padding: 0,
				margin: 0,
				display: 'flex',
				flexDirection: 'column',
				gap: 8,
				fontSize: 13,
			}}
		>
			{data.map((d) => (
				<li key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<span
						aria-hidden="true"
						style={{
							width: 10,
							height: 10,
							borderRadius: 3,
							background: d.color,
							flex: '0 0 auto',
						}}
					/>
					<span style={{ flex: 1, color: 'var(--text-dim)' }}>{d.name}</span>
					<span
						style={{
							fontVariantNumeric: 'tabular-nums',
							fontWeight: 600,
							color: 'var(--text)',
						}}
					>
						{valueFormatter(d.value)}
					</span>
				</li>
			))}
		</ul>
	);
}
